import dayjs from 'dayjs';
import { ArrowDownCircle, ArrowUpCircle, ArrowUpDown } from 'lucide-react';
import React from 'react';

export const getPriorityIcon = (priority: number) => {
  if (priority === 0) return <ArrowDownCircle color="green" size={16} />;
  if (priority === 1) return <ArrowUpDown color="orange" size={16} />;
  return <ArrowUpCircle color="red" size={16} />;
};

export const getPriorityText = (priority: number, t: (key: string) => string) => {
  if (priority === 0) return t('Low');
  if (priority === 1) return t('Medium');
  return t('High');
};

export const getPriorityColor = (priority: number) => {
  if (priority === 0) return 'green';
  if (priority === 1) return 'orange';
  return 'red';
};

export const formatDate = (date: string | null | undefined) => {
  if (!date) return '—';
  return dayjs(date).format('MMM D, YYYY');
};

export const getTimeRemaining = (dueDate: string | null | undefined, t: (key: string) => string) => {
  if (!dueDate) return null;
  const now = dayjs();
  const due = dayjs(dueDate);
  const daysRemaining = due.diff(now, 'day');

  if (daysRemaining < 0) {
    return { text: t('Overdue'), color: 'red' };
  } else if (daysRemaining === 0) {
    return { text: t('DueToday'), color: 'orange' };
  } else if (daysRemaining <= 2) {
    return { text: t('DueSoon'), color: 'orange' };
  } else {
    return { text: t('OnTrack'), color: 'green' };
  }
};

export const isOverdue = (dueDate: string | null | undefined) => {
  if (!dueDate) return false;
  return dayjs(dueDate).isBefore(dayjs(), 'day');
};

export const safeParseEditorData = (raw: string): any => {
  if (!raw) return { blocks: [] };
  try {
    return JSON.parse(raw);
  } catch {
    return { blocks: [] };
  }
};

export const priorityOptions = (t: (key: string) => string) => [
  {
    value: 0,
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ArrowDownCircle color="green" size={16} /> {t('Low')}
      </div>
    ),
  },
  {
    value: 1,
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ArrowUpDown color="orange" size={16} /> {t('Medium')}
      </div>
    ),
  },
  {
    value: 2,
    label: (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <ArrowUpCircle color="red" size={16} /> {t('High')}
      </div>
    ),
  },
];

export const getPlainTextFromEditorData = (
  raw: string,
  maxChars = 180
): string => {
  if (!raw) return '';

  const sanitize = (value: unknown): string => {
    if (!value) return '';

    if (typeof value === 'object') {
      const possibleText =
        (value as any).text ||
        (value as any).label ||
        (value as any).title;
      if (typeof possibleText === 'string') value = possibleText;
      else return ''; // unknown object → drop silently
    }

    const html = String(value);
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || '').replace(/\s+/g, ' ').trim();
  };

  try {
    const { blocks } = JSON.parse(raw);
    if (!Array.isArray(blocks)) return sanitize(raw).slice(0, maxChars);

    let out = '';

    for (const block of blocks) {
      switch (block.type) {
        case 'paragraph':
        case 'header':
          out += sanitize(block.data.text) + ' ';
          break;

        case 'list':
          if (Array.isArray(block.data.items)) {
            out += block.data.items.map(sanitize).join(', ') + ' ';
          }
          break;

        default:
          out += sanitize(block.data?.text) + ' ';
      }

      if (out.length > maxChars) break;
    }

    out = out.trim();
    return out.length > maxChars ? out.slice(0, maxChars - 1) + '…' : out;
  } catch {
    return sanitize(raw).slice(0, maxChars);
  }
};
